import { useState, useEffect } from 'react';
import Reveal from '@/components/base/Reveal';
import SmartLink from '@/components/base/SmartLink';
import Logo from '@/components/base/Logo';
import Footer from '@/pages/home/components/Footer';

function ScrollNavbar() {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 60);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { label: 'Our Mission', href: '/mission' },
    { label: 'What We Do', href: '/projects' },
    { label: 'About Us', href: '/about' },
  ];

  return (
    <div
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${scrolled ? 'bg-black/90 backdrop-blur-md border-b border-white/5' : ''
        }`}
    >
      <div className="px-6 md:px-12 lg:px-16 pt-6 pb-4">
        <nav
          className={`rounded-xl px-4 py-2 flex items-center justify-between transition-all duration-300 ${scrolled ? 'bg-transparent' : 'liquid-glass'
            }`}
        >
          <SmartLink href="/">
            <Logo className="h-5 sm:h-6 md:h-7 w-auto text-white shrink-0" />
          </SmartLink>
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map(link => (
              <SmartLink
                key={link.label}
                href={link.href}
                className={`text-sm transition-colors duration-200 whitespace-nowrap cursor-pointer ${link.href === '/about' ? 'text-white' : 'text-white/80 hover:text-gray-300'
                  }`}
              >
                {link.label}
              </SmartLink>
            ))}
          </div>
          <a href="mailto:hello@sapphiremedia.group">
            <button className="bg-white text-black px-3 py-1.5 sm:px-5 sm:py-2 md:px-6 rounded-lg text-xs sm:text-sm font-medium hover:bg-gray-100 transition-colors duration-200 whitespace-nowrap cursor-pointer inline-flex items-center gap-1 sm:gap-1.5">
              Reach out
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.75} stroke="currentColor" className="size-3.5 sm:size-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 6H5.25A2.25 2.25 0 0 0 3 8.25v10.5A2.25 2.25 0 0 0 5.25 21h10.5A2.25 2.25 0 0 0 18 18.75V10.5m-10.5 6L21 3m0 0h-5.25M21 3v5.25" />
              </svg>
            </button>
          </a>
        </nav>
      </div>
    </div>
  );
}

// TODO: replace with real team data when available
type TeamMember = {
  initial: string;
  name: string;
  role: string;
  location?: string;
  tag: string;
  image?: string;
};

const team: TeamMember[] = [
  { initial: 'J', name: 'Julian', role: 'Founder & Director', location: 'The Netherlands', tag: 'Strategy · Radio', image: "/team/juls.jpg" },
  { initial: 'M', name: 'Mohammed', role: 'Lead Engineer', location: 'Morocco', tag: 'Software · Infra', image: "/team/momo.jpg" },
  { initial: 'R', name: 'Rhea', role: 'Community Lead', location: 'Scotland', tag: 'Discord · Outreach', image: "/team/riri.jpg" },
  // { initial: '🫵', name: 'This could be you', role: 'Join our team', tag: 'On-air · Editorial' },
];

const stats = [
  { value: 'Since 2018', label: 'On the air' },
  { value: '12+', label: 'Active contributors' },
  { value: '4', label: 'Time zones' },
  { value: '501(c)3', label: 'Fiscally sponsored' },
];

export default function AboutPage() {
  return (
    <div className="bg-black min-h-screen">
      <ScrollNavbar />

      {/* 1 — Hero */}
      <section className="relative pt-44 pb-20 px-6 md:px-12 lg:px-16">
        <div className="max-w-7xl mx-auto lg:grid lg:grid-cols-2 lg:gap-20 lg:items-end">
          <div>
            <Reveal variant="fadeUp" duration={800}>
              <p className="text-xs tracking-[0.3em] text-white/60 uppercase mb-6">About Us</p>
            </Reveal>
            <Reveal variant="fadeUp" delay={100} duration={900}>
              <h1
                className="text-5xl md:text-6xl lg:text-7xl font-normal text-white font-serif"
                style={{ letterSpacing: '-0.02em' }}
              >
                Built by youth,<br />run on belief.
              </h1>
            </Reveal>
          </div>
          <Reveal variant="fadeUp" delay={240} duration={800}>
            <p className="text-base text-white/55 leading-relaxed mt-10 lg:mt-0 max-w-md">
              Sapphire Media Group is a youth-led non-profit collective making software, stories, and live radio. We started as a Discord server and a single microphone — we've kept the energy and grown the toolkit.
            </p>
          </Reveal>
        </div>

        {/* Stats strip */}
        <Reveal variant="fadeUp" delay={360} duration={800}>
          <div className="max-w-7xl mx-auto mt-20 grid grid-cols-2 lg:grid-cols-4 gap-px bg-white/5">
            {stats.map(stat => (
              <div key={stat.label} className="bg-black px-8 py-7">
                <p className="text-2xl md:text-3xl font-light text-white mb-1" style={{ letterSpacing: '-0.04em' }}>
                  {stat.value}
                </p>
                <p className="text-xs text-white/50">{stat.label}</p>
              </div>
            ))}
          </div>
        </Reveal>
      </section>

      {/* 2 — Origin story */}
      <section className="py-24 px-6 md:px-12 lg:px-16 bg-[#080808] border-t border-white/5">
        <div className="max-w-7xl mx-auto lg:grid lg:grid-cols-2 lg:gap-24 lg:items-start">
          <div className="lg:sticky lg:top-32">
            <Reveal variant="fadeRight" duration={800}>
              <p className="text-xs tracking-[0.3em] text-white/60 uppercase mb-6">Where it started</p>
            </Reveal>
            <Reveal variant="fadeRight" delay={100} duration={900}>
              <h2 className="text-4xl md:text-5xl font-normal text-white font-serif" style={{ letterSpacing: '-0.02em' }}>
                A Discord, a mic,<br />and a lot of nerve.
              </h2>
            </Reveal>
          </div>
          <div className="mt-12 lg:mt-0 space-y-6 text-white/65 leading-relaxed text-[15px] max-w-xl">
            <Reveal variant="fadeLeft" delay={120} duration={800}>
              <p>
                Sapphire began with a small group of teenagers who wanted to make things on the internet that didn't suck — open-source tools, honest radio, and projects that took young people seriously instead of patronising them.
              </p>
            </Reveal>
            <Reveal variant="fadeLeft" delay={220} duration={800}>
              <p>
                Today we run a live radio station, ship open-source software trusted by other student communities, and help young people get their first credits — on a show, in a repo, or in a byline. Adults coach, fund, and unblock; the work is done by us.
              </p>
            </Reveal>
            <Reveal variant="fadeLeft" delay={320} duration={800}>
              <p>
                We're fiscally sponsored by The Hack Foundation, a U.S. 501(c)3, which means donations are tax-deductible and the books stay clean.
              </p>
            </Reveal>
          </div>
        </div>
      </section>


      {/* 3 — Team */}
      <section className="py-24 px-6 md:px-12 lg:px-16 bg-black border-t border-white/5">
        <div className="max-w-7xl mx-auto">
          <Reveal variant="fadeUp" duration={700}>
            <p className="text-xs tracking-[0.3em] text-white/60 uppercase mb-4">The People</p>
          </Reveal>
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-14">
            <Reveal variant="fadeUp" delay={80} duration={900}>
              <h2 className="text-4xl md:text-5xl font-normal text-white font-serif" style={{ letterSpacing: '-0.02em' }}>
                Young, curious,<br />and everywhere.
              </h2>
            </Reveal>
            <Reveal variant="fadeUp" delay={160} duration={800}>
              <p className="text-sm text-white/50 max-w-xs leading-relaxed">
                A small core team across multiple countries, with a wider community of contributors on Discord.
              </p>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5">
            {team.map((member, i) => (
              <Reveal key={member.name} variant="fadeUp" delay={i * 100} duration={800} threshold={0.1}>
                <div className="bg-black group hover:bg-white/[0.02] transition-colors duration-300 h-full">
                  <div className="relative aspect-[4/5] flex items-center justify-center bg-gradient-to-br from-white/[0.04] to-white/[0.01] border-b border-white/5 overflow-hidden">
                    <span
                      className="text-7xl text-white/30 font-serif group-hover:text-white/50 transition-colors duration-300"
                      style={{ letterSpacing: '-0.02em' }}
                      aria-hidden={member.image ? 'true' : undefined}
                    >
                      {member.initial}
                    </span>
                    {member.image && (
                      <img
                        src={member.image}
                        alt={member.name}
                        loading="lazy"
                        className="absolute inset-0 w-full h-full object-cover transition-opacity duration-300"
                        onError={e => {
                          // Image failed: hide it so the initial fallback shows through.
                          (e.currentTarget as HTMLImageElement).style.display = 'none';
                        }}
                      />
                    )}
                  </div>
                  <div className="p-7">
                    <h3 className="text-base font-medium text-white mb-1">{member.name}</h3>
                    <p className="text-xs text-white/50 mb-4">
                      {member.role}{member.location && <>, from {member.location}</>}
                    </p>
                    <p className="text-[11px] text-white/35 tracking-wide uppercase">{member.tag}</p>
                  </div>
                </div>
              </Reveal>
            ))}

            {/* Join CTA — sits in the 4th grid slot to round out the row */}
            <Reveal variant="fadeUp" delay={team.length * 100} duration={800} threshold={0.1}>
              <a
                href="mailto:hello@sapphiremedia.group?subject=Joining%20the%20team"
                className="block bg-black group hover:bg-white/[0.02] transition-colors duration-300 h-full cursor-pointer"
              >
                <div className="relative aspect-[4/5] flex items-center justify-center bg-gradient-to-br from-white/[0.04] to-white/[0.01] border-b border-white/5 overflow-hidden">
                  <span
                    className="text-7xl text-white/25 font-serif group-hover:text-white/45 transition-colors duration-300"
                    style={{ letterSpacing: '-0.02em' }}
                    aria-hidden="true"
                  >
                    <img className="w-12" src="/logomark.png" alt="logomark" />
                  </span>
                </div>
                <div className="p-7">
                  <h3 className="text-base font-medium text-white mb-1">This could be you</h3>
                  <p className="text-xs text-white/50 mb-4 leading-relaxed">
                    Pitch your talent, ship code, or write a story. Email{' '}
                    <span className="text-white/70">hello@sapphiremedia.group</span> with what
                    you'd like to bring.
                  </p>
                  <p className="text-[11px] text-white/35 tracking-wide uppercase inline-flex items-center gap-1.5 group-hover:text-white/55 transition-colors duration-200">
                    Reach out
                    <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.75} stroke="currentColor" className="size-3">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 8.25 21 12m0 0-3.75 3.75M21 12H3" />
                    </svg>
                  </p>
                </div>
              </a>
            </Reveal>
          </div>

          <Reveal variant="fadeUp" delay={420} duration={800}>
            <p className="text-xs text-white/40 mt-8 max-w-xl leading-relaxed">
              Want to meet the rest? Most of the team hangs out in our Discord — <a className="text-white/75 underline hover:text-white" href="https://discord.gg/K2zNSVTT9e">drop in any time</a>.
            </p>
          </Reveal>
        </div>
      </section>

      {/* 4 — CTA */}
      <section className="py-28 px-6 md:px-12 lg:px-16 bg-[#080808] border-t border-white/5">
        <div className="max-w-4xl mx-auto text-center">
          <Reveal variant="fadeUp" duration={900}>
            <p className="text-xs tracking-[0.3em] text-white/60 uppercase mb-6">Get in touch</p>
          </Reveal>
          <Reveal variant="fadeUp" delay={120} duration={950}>
            <h2
              className="text-4xl md:text-5xl lg:text-6xl font-normal text-white mb-8 font-serif"
              style={{ letterSpacing: '-0.02em' }}
            >
              Say hi. We're<br />friendlier than we look.
            </h2>
          </Reveal>
          <Reveal variant="scaleUp" delay={260} duration={800}>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="https://discord.gg/K2zNSVTT9e"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-black px-10 py-4 rounded-lg font-medium hover:bg-gray-100 transition-colors duration-200 whitespace-nowrap cursor-pointer text-sm"
              >
                Join the Discord
              </a>
              <a
                href="mailto:hello@sapphiremedia.group"
                className="border border-white/20 text-white/70 px-10 py-4 rounded-lg font-medium hover:border-white/40 hover:text-white transition-all duration-200 whitespace-nowrap cursor-pointer text-sm"
              >
                Email us
              </a>
            </div>
          </Reveal>
        </div>
      </section>

      <Footer />
    </div>
  );
}
